import React from "react";
import { createRoot } from "react-dom/client";
import { v4 as uuidv4 } from "uuid";
import App from "./App";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  from,
  ApolloLink,
} from "@apollo/client";

import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { BrowserRouter as Router } from "react-router-dom";
import "./index.css";
import "react-modern-drawer/dist/index.css";
import authStore from "stores/authStore";
import { errorToast } from "components/toast/toast";
import { clearAccessToken, getAccessToken } from "auth/token";
import { clearUserScopedState } from "auth/session";
import { runRecoveryMigration } from "utils/recoveryMigration";
import { BUILD_VERSION, getApiHost, logBuildDiagnostics } from "config/buildVersion";
import VersionWatcher from "components/layout/VersionWatcher";

// Must run before the Apollo client / MobX stores below do anything with
// browser storage, so a stale build never gets a chance to read old data.
runRecoveryMigration();
logBuildDiagnostics();

const handleAuthError = () => {
  clearAccessToken(); // NEW: also clear the cookie
  // `client` is assigned further down this module — safe here because this
  // callback only ever runs from an async network response, which happens
  // after the whole module (including the `client` assignment) has executed.
  // Deliberately not awaited: the line right below does a full page
  // navigation, which destroys this JS context regardless of whether the
  // cache-clear promise has settled — unlike the SPA-only login/logout
  // paths, there's no continuation here for a slow clearStore() to race.
  clearUserScopedState(client);
  authStore.setIsAuthenticated(false);
  window.location.href = "/";
};

// Attaches a per-request correlation id so a failure can be traced through
// logs without needing to log any request/response payload.
const correlationLink = setContext((_, prevContext) => ({
  ...prevContext,
  headers: {
    ...prevContext.headers,
    "x-request-id": uuidv4(),
  },
}));

// Safe diagnostics only: build version, API host, operation name, HTTP
// status, and the request id above. Never logs tokens, headers, variables,
// or response data/errors' payloads.
const diagnosticsLink = onError(
  ({ operation, networkError, graphQLErrors }) => {
    if (!networkError && !graphQLErrors?.length) return;

    const requestId = operation.getContext()?.headers?.["x-request-id"];
    // eslint-disable-next-line no-console
    console.warn("[travelbay] request diagnostics", {
      build: BUILD_VERSION,
      apiHost: getApiHost(),
      operationName: operation.operationName,
      httpStatus: networkError?.statusCode ?? null,
      requestId,
      timestamp: new Date().toISOString(),
    });
  },
);

// A single non-blocking toast per failed response — not one alert() per
// GraphQL error. Screens with their own retry banner (see RetryBanner.js)
// already surface the failure inline; this is just a global fallback for
// screens that don't yet have one.
const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors?.length) {
    const message = graphQLErrors.map((err) => err.message).join(" ");
    errorToast("Error", message);
  }
});

const authResponseLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    // Only protocol-level GraphQL errors count here — scanning response.data
    // as well causes false positives whenever legitimate content (e.g. a
    // BadRequest message for an optional-auth field) happens to contain this
    // wording, forcing out users who are still validly logged in.
    const hasAuthError = response.errors?.some((err) => {
      const message = err.message?.toLowerCase() || "";
      return (
        message.includes("not authenticated") ||
        message.includes("token expired") ||
        message.includes("unauthorized")
      );
    });

    // Deliberately not gated on getAccessToken() — Pages (routes/index.js)
    // isn't a MobX observer, so setIsAuthenticated(false) alone can't repaint
    // it. The hard redirect below is what actually clears a stuck private
    // route once the cookie is already gone, so it must still fire in that case.
    if (hasAuthError) {
      handleAuthError();
    }

    return response;
  });
});

// NEW: inject Authorization header from cookie
const authLink = setContext((_, { headers }) => {
  const token = getAccessToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

const link = from([
  authResponseLink,
  diagnosticsLink,
  errorLink,
  correlationLink,
  authLink,
  new HttpLink({
    uri: process.env.REACT_APP_GRAPHQL_URL,
    credentials: "include",
    fetchOptions: {
      credentials: "include",
    },
  }),
]);

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: link,

  // link: authLink.concat(link),
});

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <ApolloProvider client={client} className="font-sans">
    <Router>
      <VersionWatcher />
      <App />
    </Router>
  </ApolloProvider>,
);
